import { formatScheduledLocal } from '@aegislab/shared';
import type {
  OutreachRoundCancelReason,
  OutreachRoundHistoryItem,
  OutreachRoundStatus,
} from '@aegislab/shared';
import { Button } from '@/shared/ui/atoms';
import * as s from './OutreachHistoryPanel.css';

export interface OutreachHistoryPanelProps {
  /** 목록을 부르는 중이거나 못 불렀을 때 빈 표를 「보낸 회차 없음」 으로 읽히지 않게 가른다. */
  state?: 'loading' | 'error' | 'ready';
  items: readonly OutreachRoundHistoryItem[];
  /** 아직 안 나간 예약 회차의 취소(96445435). 없으면 취소 버튼을 그리지 않는다. */
  onCancelReservation?: (roundId: string) => void;
  /** 취소를 보내는 중인 회차. */
  cancellingId?: string | null;
}

/** 상태 배지의 글자와 표면. 결과를 모르는 회차는 실패가 아니라 확인 대기다. */
const STATUS_VIEW: Record<
  OutreachRoundStatus,
  { label: string; tone: 'sent' | 'wait' | 'neutral' }
> = {
  SENT: { label: '발송 완료', tone: 'sent' },
  ACCEPTED: { label: '결과 확인 대기', tone: 'wait' },
  INCOMPLETE: { label: '결과 확인 대기', tone: 'wait' },
  FAILED: { label: '결과 확인 대기', tone: 'wait' },
  SCHEDULING: { label: '예약 처리 중', tone: 'neutral' },
  RESERVED: { label: '예약 대기', tone: 'neutral' },
  RESERVATION_UNCONFIRMED: { label: '예약 확인 필요', tone: 'wait' },
  CANCELLED: { label: '취소됨', tone: 'neutral' },
};

const CANCEL_REASON: Record<OutreachRoundCancelReason, string> = {
  USER: '실행한 사람이 취소했습니다.',
  ALL_EXCLUDED: '받을 사람이 모두 빠져 취소했습니다.',
  FORM_WATCH: '설문폼 응답 감시가 멈춰 취소했습니다.',
};

const CANCELLABLE: ReadonlySet<OutreachRoundStatus> = new Set(['SCHEDULING', 'RESERVED']);

function exclusionOf(item: OutreachRoundHistoryItem): string {
  return [
    item.excludedCount > 0 ? `예약 뒤 빠짐 ${fmt(item.excludedCount)}` : null,
    item.lateCount > 0 ? `마감 후 도착 ${fmt(item.lateCount)}` : null,
  ]
    .filter((part) => part !== null)
    .join(' · ');
}

const numberFormat = new Intl.NumberFormat('ko-KR');

function formatDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

function formatMoment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${formatDay(iso)} ${hh}:${mm}`;
}

const fmt = (value: number) => numberFormat.format(value);

/**
 * 대상 칸 아래 줄 — 왜 요청보다 적게 닿았는지. 손입력 회차는 붙여넣은 핸들 중 주소를 못 찾은 수를,
 * 대량 회차는 서버가 센 주소 없음 · 원천에서 못 찾음을 적는다.
 */
function shortfallOf(item: OutreachRoundHistoryItem): string {
  const parts =
    item.sequence === null
      ? [
          `요청 ${fmt(item.requestedHandles.length)}`,
          `주소 없음 ${fmt(item.unmatchedHandles.length)}`,
        ]
      : [
          item.noEmailCount > 0 ? `주소 없음 ${fmt(item.noEmailCount)}` : null,
          item.unresolvedCount > 0 ? `원천에서 못 찾음 ${fmt(item.unresolvedCount)}` : null,
        ];
  return parts.filter((part) => part !== null).join(' · ');
}

/**
 * 발송 이력 — IA `docs/design/ia/creator-recruitment-ia.md` §3-6.
 *
 * **회차별 표**다. 한 번 누른 발송이 1,000명씩 쪼개져도 이력은 회차로 나뉜다.
 *
 * **번호를 화면에서 다시 매기지 않는다.** 서버가 저장한 나라별 번호를 그대로 쓴다 — 목록에서
 * 세면 손입력 회차가 섞여 번호가 어긋난다.
 *
 * **실패한 회차에 재발송 버튼을 두지 않는다**(IA §3-6). 같은 대상을 다시 보내는 길은 후보
 * 선택으로 돌아가 새 명단을 만드는 것 하나뿐이다.
 */
export function OutreachHistoryPanel({
  state = 'ready',
  items,
  onCancelReservation,
  cancellingId = null,
}: OutreachHistoryPanelProps) {
  return (
    <section className={s.panel} aria-labelledby="outreach-history-heading">
      <div className={s.head}>
        <h3 id="outreach-history-heading" className={s.heading}>
          발송 이력 {state === 'ready' ? `${numberFormat.format(items.length)}회차` : ''}
        </h3>
      </div>

      <div className={s.frame}>
        {state === 'loading' ? (
          <p className={s.empty}>발송 이력을 불러오는 중입니다.</p>
        ) : state === 'error' ? (
          <p className={s.empty}>발송 이력을 불러오지 못했습니다.</p>
        ) : items.length === 0 ? (
          <p className={s.empty}>아직 보낸 회차가 없습니다.</p>
        ) : (
          <table className={s.table}>
            <caption className={s.srOnly}>발송 회차 목록</caption>
            <thead>
              <tr>
                <th scope="col" className={s.th}>
                  회차
                </th>
                <th scope="col" className={s.th}>
                  제목
                </th>
                <th scope="col" className={s.th}>
                  실행
                </th>
                <th scope="col" className={s.th}>
                  실제 발송
                </th>
                <th scope="col" className={`${s.th} ${s.numeric}`}>
                  대상
                </th>
                <th scope="col" className={s.th}>
                  상태
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className={s.row}>
                  <td className={s.td}>
                    <span className={s.roundCell}>
                      {item.country === null ? null : (
                        <span className={s.countryTag}>{item.country}</span>
                      )}
                      <span className={s.sequence}>
                        {item.sequence === null
                          ? '손입력'
                          : `${numberFormat.format(item.sequence)}회차`}
                      </span>
                    </span>
                  </td>

                  <td className={s.td}>
                    <span className={s.subject}>{item.subject}</span>
                    <span className={s.subLine}>
                      {item.formCount === 0
                        ? '아직 들어온 행 없음'
                        : `이 회차로 들어온 로스터 행 ${numberFormat.format(item.formCount)}`}
                    </span>
                  </td>

                  <td className={s.td}>
                    <span className={s.plain}>
                      {item.sentByName} · {formatDay(item.createdAt)}
                    </span>
                    <span className={s.subLine}>발신 {item.senderName}</span>
                  </td>

                  <td className={s.td}>
                    <span className={s.plain}>
                      {item.sentAt === null ? '아직 없음' : formatMoment(item.sentAt)}
                    </span>
                    {item.scheduledAt === null || item.scheduledTimeZone === null ? null : (
                      <span className={s.subLine}>
                        예약 {formatScheduledLocal(item.scheduledAt, item.scheduledTimeZone)} ·{' '}
                        {item.scheduledTimeZone}
                      </span>
                    )}
                  </td>

                  <td className={`${s.td} ${s.numeric}`}>
                    <span className={s.target}>{numberFormat.format(item.matchedCount)}명</span>
                    {shortfallOf(item) === '' ? null : (
                      <span className={s.subLine}>{shortfallOf(item)}</span>
                    )}
                    {exclusionOf(item) === '' ? null : (
                      <span className={s.subLine}>{exclusionOf(item)}</span>
                    )}
                  </td>

                  <td className={s.td}>
                    <span className={s.statusStack}>
                      <span>
                        {item.isTest ? <span className={s.testPill}>테스트</span> : null}
                        <span
                          className={
                            STATUS_VIEW[item.status].tone === 'sent'
                              ? s.sentPill
                              : STATUS_VIEW[item.status].tone === 'neutral'
                                ? s.neutralPill
                                : s.waitPill
                          }
                        >
                          {STATUS_VIEW[item.status].label}
                        </span>
                      </span>
                      {item.status === 'CANCELLED' && item.cancelReason !== null ? (
                        <span className={s.subLine}>{CANCEL_REASON[item.cancelReason]}</span>
                      ) : null}
                      {onCancelReservation && CANCELLABLE.has(item.status) ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => onCancelReservation(item.id)}
                          disabled={cancellingId === item.id}
                        >
                          {cancellingId === item.id ? '취소하는 중' : '예약 취소'}
                        </Button>
                      ) : null}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className={s.footNote}>
          실패한 회차에 같은 대상을 다시 보내는 버튼은 두지 않습니다 — 공급자 이력을 먼저 확인하고,
          추가 섭외는 후보 선택으로 돌아와 새 명단을 만듭니다.{' '}
          <strong className={s.strong}>결과를 확인하지 못한 회차</strong>는 실패가 아니라 확인
          대기입니다.
        </p>
      </div>
    </section>
  );
}
